import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Send, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const CAT_LABELS = { equipment_issues: 'Equipment', equipment_damage: 'Damage', maintenance: 'Maintenance', load_issues: 'Load', load_damage: 'Load Damage', load_delays: 'Delays', hr: 'HR', payroll: 'Payroll', safety: 'Safety', other: 'Other' };
const PRIORITY_COLORS = { critical: 'border-l-red-500', high: 'border-l-red-400', medium: 'border-l-amber-400', low: 'border-l-emerald-400' };
const PRIORITY_BADGE = { critical: 'bg-red-500/20 text-red-400', high: 'bg-orange-500/20 text-orange-400', medium: 'bg-amber-500/20 text-amber-400', low: 'bg-zinc-600 text-muted-foreground' };
const STATUS_BADGE = { open: 'bg-red-500/20 text-red-400', in_progress: 'bg-amber-500/20 text-amber-400', pending: 'bg-amber-500/20 text-amber-400', resolved: 'bg-emerald-500/20 text-emerald-400', closed: 'bg-zinc-600 text-muted-foreground' };

export default function AdminTickets() {
  const { token, user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open');
  const [selected, setSelected] = useState(null);
  const [comment, setComment] = useState('');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [internalNotes, setInternalNotes] = useState({});
  const [noteInput, setNoteInput] = useState('');
  const commentsEndRef = useRef(null);

  const fetchTickets = () => {
    api.get('/tickets').then(r => setTickets(r.data)).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchTickets(); }, [token]);

  const openDetail = async (ticketId) => {
    try {
      const res = await api.get(`/tickets/${ticketId}`);
      setSelected(res.data);
      setTimeout(() => commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch {}
  };

  const handleStatusChange = async (ticketId, status) => {
    try {
      await api.put(`/tickets/${ticketId}/status`, { status });
      toast.success(`Ticket ${status.replace('_', ' ')}`);
      fetchTickets();
      if (selected?.ticket_id === ticketId) openDetail(ticketId);
    } catch { toast.error('Failed'); }
  };

  const handlePriorityChange = (ticketId, newPriority) => {
    setTickets(prev => prev.map(t => t.ticket_id === ticketId ? { ...t, priority: newPriority } : t));
    if (selected?.ticket_id === ticketId) setSelected(prev => ({ ...prev, priority: newPriority }));
    toast.success(`Priority changed to ${newPriority}`);
  };

  const addInternalNote = () => {
    if (!noteInput.trim() || !selected) return;
    const ticketId = selected.ticket_id;
    const note = { id: Date.now(), text: noteInput.trim(), timestamp: new Date().toISOString(), author: user?.name || 'Admin' };
    setInternalNotes(prev => ({ ...prev, [ticketId]: [...(prev[ticketId] || []), note] }));
    setNoteInput('');
    toast.success('Internal note added');
  };

  const handleComment = async () => {
    if (!comment.trim() || !selected) return;
    try {
      await api.post(`/tickets/${selected.ticket_id}/comments`, { content: comment.trim() });
      setComment('');
      openDetail(selected.ticket_id);
    } catch {}
  };

  // Filters — removed "active", kept open/in_progress/resolved/closed/all
  let filtered = filter === 'all' ? tickets : tickets.filter(t => t.status === filter);
  if (dateStart) filtered = filtered.filter(t => new Date(t.created_at) >= new Date(dateStart));
  if (dateEnd) filtered = filtered.filter(t => new Date(t.created_at) <= new Date(dateEnd + 'T23:59:59'));

  const openCount = tickets.filter(t => t.status === 'open').length;
  const criticalCount = tickets.filter(t => t.priority === 'critical' && ['open', 'in_progress'].includes(t.status)).length;

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-brand-accent border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Support Tickets</h1>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-muted-foreground">{tickets.length} total</span>
          <span className="text-red-400">{openCount} open</span>
          {criticalCount > 0 && <span className="text-red-500 font-bold">{criticalCount} critical</span>}
        </div>
      </div>

      {/* KPI + Filters */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Total', value: tickets.length, accent: 'text-foreground' },
          { label: 'Open', value: openCount, accent: 'text-red-400' },
          { label: 'Critical', value: criticalCount, accent: 'text-red-500' },
          { label: 'Resolved', value: tickets.filter(t => t.status === 'resolved').length, accent: 'text-emerald-400' },
        ].map((s, i) => (
          <Card key={i} className="bg-card border-border"><CardContent className="p-3 text-center">
            <p className={`text-xl font-bold ${s.accent}`}>{s.value}</p>
            <p className="text-[10px] text-muted-foreground">{s.label}</p>
          </CardContent></Card>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {['open', 'in_progress', 'resolved', 'closed', 'all'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-[10px] font-bold tracking-wider capitalize transition-colors ${
              filter === f ? 'bg-brand-accent/10 text-brand-accent border border-brand-accent/20' : 'text-muted-foreground border border-border hover:text-foreground'
            }`}>{f.replace('_', ' ')}</button>
        ))}
        <div className="flex items-center gap-1.5 ml-auto">
          <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
          <Input type="date" value={dateStart} onChange={e => setDateStart(e.target.value)}
            className="bg-muted border-input text-muted-foreground h-7 text-xs w-32 rounded-lg" />
          <span className="text-muted-foreground text-xs">to</span>
          <Input type="date" value={dateEnd} onChange={e => setDateEnd(e.target.value)}
            className="bg-muted border-input text-muted-foreground h-7 text-xs w-32 rounded-lg" />
        </div>
      </div>

      {/* 3-Panel Layout */}
      <div className="flex gap-0 border border-border rounded-xl overflow-hidden" style={{ height: 'calc(100vh - 380px)', minHeight: '400px' }}>
        {/* Panel 1: Ticket List */}
        <div className="w-80 flex-shrink-0 border-r border-border overflow-y-auto bg-card">
          {filtered.map(t => (
            <div key={t.ticket_id}
              onClick={() => openDetail(t.ticket_id)}
              className={`p-3 border-b border-border border-l-4 cursor-pointer transition-colors ${PRIORITY_COLORS[t.priority]} ${
                selected?.ticket_id === t.ticket_id ? 'bg-muted/50' : 'hover:bg-muted/30'
              }`}>
              <div className="flex items-center gap-1.5 mb-1">
                <Badge className={`text-[8px] ${PRIORITY_BADGE[t.priority]}`}>{t.priority}</Badge>
                <Badge className={`text-[8px] ${STATUS_BADGE[t.status]}`}>{t.status?.replace('_', ' ')}</Badge>
                <span className="text-[10px] text-muted-foreground ml-auto">{CAT_LABELS[t.category]}</span>
              </div>
              <p className="text-xs text-foreground font-medium truncate">{t.subject}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-1">{t.description}</p>
              <p className="text-[10px] text-muted-foreground mt-1">{new Date(t.created_at).toLocaleDateString()}</p>
            </div>
          ))}
          {filtered.length === 0 && <p className="text-center text-muted-foreground text-sm py-8">No tickets</p>}
        </div>

        {/* Panel 2: Ticket Detail */}
        <div className="flex-1 border-r border-border overflow-y-auto bg-background p-4">
          {selected ? (
            <div className="space-y-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={PRIORITY_BADGE[selected.priority]}>{selected.priority}</Badge>
                  <select value={selected.priority} onChange={e => handlePriorityChange(selected.ticket_id, e.target.value)}
                    className="bg-muted border border-input text-foreground text-xs rounded-md px-2 py-1 appearance-none cursor-pointer">
                    {['critical', 'high', 'medium', 'low'].map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <div className="ml-auto">
                    <select value={selected.status} onChange={e => handleStatusChange(selected.ticket_id, e.target.value)}
                      className="bg-muted border border-input text-foreground text-xs rounded-md px-2 py-1 appearance-none cursor-pointer">
                      {['open', 'in_progress', 'resolved', 'closed'].map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                    </select>
                  </div>
                </div>
                <h2 className="text-lg font-bold text-foreground">{selected.subject}</h2>
                <p className="text-xs text-muted-foreground mt-1">{CAT_LABELS[selected.category]} · Created {new Date(selected.created_at).toLocaleDateString()} by {selected.created_by}</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selected.description}</p>
              </div>
              {selected.related_load_id && (
                <div className="bg-muted/50 rounded-lg p-2">
                  <span className="text-[10px] text-muted-foreground">Related Load:</span>
                  <span className="text-xs text-brand-accent ml-2 font-mono">{selected.related_load_id}</span>
                </div>
              )}
              {selected.related_equipment_id && (
                <div className="bg-muted/50 rounded-lg p-2">
                  <span className="text-[10px] text-muted-foreground">Related Equipment:</span>
                  <span className="text-xs text-foreground ml-2 font-mono">{selected.related_equipment_id}</span>
                </div>
              )}
              {/* Internal Notes */}
              <div className="bg-surface border border-border rounded-lg p-3">
                <p className="text-[10px] text-muted-foreground tracking-wider font-bold mb-2">INTERNAL NOTES</p>
                <div className="space-y-2 max-h-40 overflow-y-auto mb-2">
                  {(internalNotes[selected.ticket_id] || []).map(n => (
                    <div key={n.id} className="bg-muted/50 rounded-lg px-3 py-2">
                      <p className="text-xs text-foreground">{n.text}</p>
                      <p className="text-[10px] text-muted-foreground mt-1">{n.author} · {new Date(n.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  ))}
                  {(!internalNotes[selected.ticket_id] || internalNotes[selected.ticket_id].length === 0) && (
                    <p className="text-muted-foreground text-xs text-center py-2">No internal notes</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Input value={noteInput} onChange={e => setNoteInput(e.target.value)} placeholder="Add internal note..."
                    className="bg-muted border-input text-foreground text-xs" onKeyDown={e => e.key === 'Enter' && addInternalNote()} />
                  <Button onClick={addInternalNote} disabled={!noteInput.trim()} size="sm" className="bg-brand-accent text-brand-accent-foreground h-9">
                    <Send className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground text-sm">Select a ticket to view details</div>
          )}
        </div>

        {/* Panel 3: Comments */}
        <div className="w-80 flex-shrink-0 flex flex-col bg-card">
          <div className="p-3 border-b border-border">
            <p className="text-[10px] text-muted-foreground tracking-wider font-bold">COMMENTS</p>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {selected ? (selected.comments || []).map(c => (
              <div key={c.comment_id} className={`rounded-lg px-3 py-2 max-w-[95%] ${
                c.author_id === user?.user_id ? 'bg-brand-accent/10 border border-brand-accent/20 ml-auto' : 'bg-muted'
              }`}>
                {c.author_id !== user?.user_id && (
                  <p className="text-[10px] font-bold text-muted-foreground mb-0.5">{c.author_name} <span className="text-muted-foreground font-normal">({c.author_role})</span></p>
                )}
                <p className="text-xs text-foreground">{c.content}</p>
                <p className={`text-[10px] mt-1 ${c.author_id === user?.user_id ? 'text-brand-accent/60' : 'text-muted-foreground'}`}>
                  {new Date(c.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            )) : null}
            {selected && (!selected.comments || selected.comments.length === 0) && (
              <p className="text-muted-foreground text-xs text-center py-4">No comments yet</p>
            )}
            <div ref={commentsEndRef} />
          </div>
          {selected && (
            <div className="p-3 border-t border-border flex gap-2">
              <Input value={comment} onChange={e => setComment(e.target.value)} placeholder="Add comment..."
                className="bg-muted border-input text-foreground text-xs" onKeyDown={e => e.key === 'Enter' && handleComment()} />
              <Button onClick={handleComment} disabled={!comment.trim()} size="sm" className="bg-brand-accent text-brand-accent-foreground h-9">
                <Send className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
